// app/javascript/controllers/inquiry_form_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "submitButton", "responseMessage"]

  connect() {
    // Initialize the form - no need for phone field format hint code now
  }

  // Allow only numbers in phone field
  allowOnlyNumbers(event) {
    // Allow only digits, backspace, tab, etc.
    const keyCodes = [8, 9, 37, 39, 46]; // Backspace, Tab, arrows, delete
    if (keyCodes.indexOf(event.keyCode) !== -1) return;
    
    // Allow only digits (0-9)
    if (event.key < '0' || event.key > '9') {
      event.preventDefault();
    }
  }

  // Validate a specific field on input
  validateField(event) {
    const input = event.currentTarget;
    
    // Clear previous errors
    this.clearErrorForInput(input);
    
    // Validation based on input type
    if (input.id === 'inquiry_email') {
      const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (input.value && !emailPattern.test(input.value.trim())) {
        this.showError(input, "Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      }
    }
    
    if (input.required && !input.value.trim()) {
      const fieldName = input.previousElementSibling?.textContent || "Dieses Feld";
      this.showError(input, `${fieldName} ist erforderlich.`);
    }
  }

  // Special validation for phone field
  validatePhoneField(event) {
    const input = event.currentTarget;
    this.clearErrorForInput(input);
    
    if (input.value) {
      const phonePattern = /^0\d{4,}$|^\d{5,}$/;
      if (!phonePattern.test(input.value.trim())) {
        this.showError(input, "Bitte geben Sie eine gültige Telefonnummer ein.");
      }
    } else if (input.required) {
      this.showError(input, "Telefonnummer ist erforderlich.");
    }
  }
  
  // Validate service selection
  validateServiceSelection() {
    const checkboxes = this.element.querySelectorAll('.service-checkbox');
    const checked = Array.from(checkboxes).some(cb => cb.checked);
    const servicesError = this.element.querySelector('#services-error');
    
    if (!checked) {
      if (servicesError) {
        servicesError.classList.remove('hidden');
      }
    } else {
      if (servicesError) {
        servicesError.classList.add('hidden');
      }
    }
  }

  validateForm(event) {
    const form = event.currentTarget;
    let valid = true;

    // Remove previous error messages
    form.querySelectorAll('.form-error-message').forEach(el => el.remove());
    form.querySelectorAll('.border-red-500').forEach(el => el.classList.remove('border-red-500', 'bg-red-50'));

    // Validate required text fields
    const requiredFields = [
      { selector: '[name="inquiry[first_name]"]', message: "Vorname ist erforderlich." },
      { selector: '[name="inquiry[last_name]"]', message: "Nachname ist erforderlich." },
      { selector: '[name="inquiry[email]"]', message: "E-Mail ist erforderlich." },
      { selector: '[name="inquiry[phone]"]', message: "Telefon ist erforderlich." }
    ]
    requiredFields.forEach(field => {
      const input = form.querySelector(field.selector);
      if (!input || !input.value.trim()) {
        this.showError(input, field.message);
        valid = false;
      }
    })

    // Email format
    const emailInput = form.querySelector('[name="inquiry[email]"]');
    if (emailInput && emailInput.value) {
      const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!emailPattern.test(emailInput.value.trim())) {
        this.showError(emailInput, "Bitte geben Sie eine gültige E-Mail-Adresse ein.");
        valid = false;
      }
    }

    // Phone format - update the pattern here too
    const phoneInput = form.querySelector('[name="inquiry[phone]"]');
    if (phoneInput && phoneInput.value) {
      const phonePattern = /^0\d{4,}$|^\d{5,}$/;
      if (!phonePattern.test(phoneInput.value.trim())) {
        this.showError(phoneInput, "Bitte geben Sie eine gültige Telefonnummer ein.");
        valid = false;
      }
    }

    // At least one service selected
    const checkboxes = form.querySelectorAll('.service-checkbox');
    const checked = Array.from(checkboxes).some(cb => cb.checked);
    const servicesError = form.querySelector('#services-error');
    if (!checked) {
      if (servicesError) {
        servicesError.classList.remove('hidden');
      }
      valid = false;
    } else {
      if (servicesError) {
        servicesError.classList.add('hidden');
      }
    }

    if (!valid) {
      event.preventDefault();
      event.stopPropagation();
      
      // Scroll to first error
      const firstError = form.querySelector('.border-red-500');
      if (firstError) {
        firstError.focus();
      }
      
      return false;
    }

    // Disable submit button to prevent double submissions
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('opacity-75');

      // Change button text
      const originalText = submitButton.innerHTML;
      submitButton.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Wird gesendet...
      `

      // Reset button after 5 seconds if the form hasn't been submitted
      setTimeout(() => {
        if (!submitButton.disabled) return; // Don't reset if already re-enabled
        submitButton.disabled = false;
        submitButton.classList.remove('opacity-75');
        submitButton.innerHTML = originalText;
      }, 5000)
    }

    return true;
  }

  validateHomeForm(event) {
    const form = event.currentTarget;
    let valid = true;

    // Remove previous error messages
    form.querySelectorAll('.form-error-message').forEach(el => el.remove());
    form.querySelectorAll('.border-red-500').forEach(el => el.classList.remove('border-red-500', 'bg-red-50'));

    // Validate required text fields
    const requiredFields = [
      { selector: '[name="inquiry[first_name]"]', message: "Vorname ist erforderlich." },
      { selector: '[name="inquiry[last_name]"]', message: "Nachname ist erforderlich." },
      { selector: '[name="inquiry[email]"]', message: "E-Mail ist erforderlich." },
      { selector: '[name="inquiry[phone]"]', message: "Telefon ist erforderlich." }
    ]
    requiredFields.forEach(field => {
      const input = form.querySelector(field.selector);
      if (!input || !input.value.trim()) {
        this.showError(input, field.message);
        valid = false;
      }
    })

    // Email format
    const emailInput = form.querySelector('[name="inquiry[email]"]');
    if (emailInput && emailInput.value) {
      const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!emailPattern.test(emailInput.value.trim())) {
        this.showError(emailInput, "Bitte geben Sie eine gültige E-Mail-Adresse ein.");
        valid = false;
      }
    }

    // Phone format - update the pattern here too
    const phoneInput = form.querySelector('[name="inquiry[phone]"]');
    if (phoneInput && phoneInput.value) {
      const phonePattern = /^0\d{4,}$|^\d{5,}$/;
      if (!phonePattern.test(phoneInput.value.trim())) {
        this.showError(phoneInput, "Bitte geben Sie eine gültige Telefonnummer ein.");
        valid = false;
      }
    }

    if (!valid) {
      event.preventDefault();
      event.stopPropagation();
      
      // Scroll to first error
      const firstError = form.querySelector('.border-red-500');
      if (firstError) {
        firstError.focus();
      }
      
      return false;
    }

    // Disable submit button to prevent double submissions
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('opacity-75');

      // Change button text
      const originalText = submitButton.innerHTML;
      submitButton.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Wird gesendet...
      `

      // Reset button after 5 seconds if the form hasn't been submitted
      setTimeout(() => {
        if (!submitButton.disabled) return; // Don't reset if already re-enabled
        submitButton.disabled = false;
        submitButton.classList.remove('opacity-75');
        submitButton.innerHTML = originalText;
      }, 5000)
    }

    return true;
  }

  showError(input, message) {
    if (!input) return;
    input.classList.add('border-red-500');
    
    // More visible styling for error state
    if (input.parentNode.classList.contains('flex')) {
      // For phone field which is in a flex container
      input.parentNode.classList.add('border-red-500');
    }
    
    const errorMessage = document.createElement('p');
    errorMessage.className = 'text-red-300 text-sm mt-1 form-error-message';
    errorMessage.textContent = message;
    
    // Only add if not already present
    if (!input.nextElementSibling || !input.nextElementSibling.classList.contains('form-error-message')) {
      // For phone field in a flex container, add after the parent
      if (input.parentNode.classList.contains('flex')) {
        input.parentNode.parentNode.insertBefore(errorMessage, input.parentNode.nextSibling);
      } else {
        input.parentNode.insertBefore(errorMessage, input.nextSibling);
      }
    }
  }
  
  clearErrorForInput(input) {
    if (!input) return;
    
    // Remove error styling
    input.classList.remove('border-red-500');
    
    // For phone field in a flex container
    if (input.parentNode.classList.contains('flex')) {
      input.parentNode.classList.remove('border-red-500');
    }
    
    // Remove error message
    let errorMessage;
    if (input.parentNode.classList.contains('flex')) {
      // For phone field, error is after the parent
      errorMessage = input.parentNode.nextElementSibling;
      if (errorMessage && errorMessage.classList.contains('form-error-message')) {
        errorMessage.remove();
      }
    } else {
      // For regular fields
      errorMessage = input.nextElementSibling;
      if (errorMessage && errorMessage.classList.contains('form-error-message')) {
        errorMessage.remove();
      }
    }
  }
}